import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const emptyService = {
  id: null,
  title: '',
  shortDesc: '',
  fullDesc: '',
  image: '',
  slug: '',
  featuresText: '',
  isActive: true,
  displayOrder: '',
};

const ServicesSettings = () => {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(emptyService);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadServices = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_BASE_URL}/api/admin/services`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.services || [];
      setServices(
        list.map((s) => ({
          ...s,
          featuresText: Array.isArray(s.features) ? s.features.join('\n') : '',
          displayOrder: s.displayOrder ?? '',
        }))
      );
    } catch (err) {
      const message = err.response?.data?.message || 'تعذر تحميل قائمة الخدمات.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const startNew = () => {
    setSelected(emptyService);
    setSuccess('');
    setError('');
  };

  const startEdit = (service) => {
    setSelected({
      id: service.id,
      title: service.title || '',
      shortDesc: service.shortDesc || '',
      fullDesc: service.fullDesc || '',
      image: service.image || '',
      slug: service.slug || '',
      featuresText: service.featuresText || '',
      isActive: service.isActive ?? true,
      displayOrder: service.displayOrder ?? '',
    });
    setSuccess('');
    setError('');
  };

  const handleChange = (field, value) => {
    setSelected((prev) => ({ ...prev, [field]: value }));
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data?.url) {
        setSelected((prev) => ({ ...prev, image: res.data.url }));
        setSuccess('تم رفع صورة الخدمة بنجاح.');
      }
    } catch (err) {
      const message =
        err.response?.data?.message || 'فشل رفع الصورة. تأكد من أن الملف صورة أقل من 5MB.';
      setError(message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      setSaving(false);
      return;
    }

    const featuresArray = selected.featuresText
      ? selected.featuresText.split('\n').map((s) => s.trim()).filter(Boolean)
      : [];

    const payload = {
      title: selected.title,
      shortDesc: selected.shortDesc,
      fullDesc: selected.fullDesc,
      image: selected.image,
      slug: selected.slug,
      features: featuresArray,
      isActive: !!selected.isActive,
      displayOrder: selected.displayOrder ? Number(selected.displayOrder) : undefined,
    };

    try {
      if (selected.id) {
        await axios.put(`${API_BASE_URL}/api/admin/services/${selected.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('تم تحديث الخدمة بنجاح.');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/services`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('تم إنشاء خدمة جديدة بنجاح.');
      }
      await loadServices();
      if (!selected.id) {
        setSelected(emptyService);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'حدث خطأ أثناء حفظ الخدمة.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الخدمة؟')) return;
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('تم حذف الخدمة بنجاح.');
      await loadServices();
      if (selected.id === id) {
        setSelected(emptyService);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'فشل حذف الخدمة.';
      setError(message);
    }
  };

  if (loading) {
    return <p>جاري تحميل الخدمات...</p>;
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">إدارة الخدمات</h2>
      <p className="admin-section-subtitle">
        يمكنك هنا إضافة، تعديل، أو حذف خدمات نبض كير، مع التحكم في ترتيب العرض والتفاصيل الكاملة لكل خدمة.
      </p>

      {error && <div className="admin-section-error">{error}</div>}
      {success && <div className="admin-section-success">{success}</div>}

      <div className="admin-grid-2">
        <div>
          <div className="admin-links-header">
            <h3>قائمة الخدمات</h3>
            <button type="button" className="admin-btn-secondary" onClick={startNew}>
              خدمة جديدة
            </button>
          </div>
          <div className="admin-links-list">
            {services.map((s) => (
              <div key={s.id} className="admin-link-row">
                <div className="admin-link-fields">
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => startEdit(s)}
                  >
                    #{s.id} - {s.title}
                  </button>
                </div>
                <button
                  type="button"
                  className="admin-btn-danger"
                  onClick={() => handleDelete(s.id)}
                >
                  حذف
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: '0.75rem' }}>
            {selected.id ? `تعديل الخدمة رقم ${selected.id}` : 'إضافة خدمة جديدة'}
          </h3>

          <div className="admin-field">
            <label>عنوان الخدمة</label>
            <input
              type="text"
              value={selected.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          <div className="admin-field">
            <label>Slug (اختياري، يستخدم في الروابط)</label>
            <input
              type="text"
              value={selected.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="مثال: sfda-services"
            />
          </div>

          <div className="admin-field">
            <label>الوصف المختصر</label>
            <textarea
              rows={2}
              value={selected.shortDesc}
              onChange={(e) => handleChange('shortDesc', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="admin-field">
            <label>الوصف الكامل</label>
            <textarea
              rows={4}
              value={selected.fullDesc}
              onChange={(e) => handleChange('fullDesc', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label>رابط صورة الخدمة</label>
              <input
                type="text"
                value={selected.image}
                onChange={(e) => handleChange('image', e.target.value)}
                placeholder="/uploads/service-1.webp أو رابط خارجي"
              />
              <small>يمكنك رفع صورة الخدمة أو استخدام رابط خارجي.</small>
              <div style={{ marginTop: '0.5rem' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUploadImage(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <div className="admin-field">
              <label>ترتيب العرض</label>
              <input
                type="number"
                value={selected.displayOrder}
                onChange={(e) => handleChange('displayOrder', e.target.value)}
                placeholder="1، 2، 3..."
              />
            </div>
          </div>

          <div className="admin-field">
            <label>نقاط الخدمة (كل سطر يمثل نقطة)</label>
            <textarea
              rows={4}
              value={selected.featuresText}
              onChange={(e) => handleChange('featuresText', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="admin-field" style={{ marginTop: '0.5rem' }}>
            <label>
              <input
                type="checkbox"
                checked={selected.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                style={{ marginLeft: '0.5rem' }}
              />
              الخدمة مفعّلة وتظهر في الموقع
            </label>
          </div>

          <div className="admin-actions">
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'جاري الحفظ...' : 'حفظ الخدمة'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesSettings;

